import { DEFAULT_SPEED, DEFAULT_ZOOM } from '../../../../components/mapbox/utils/markers-manager';
import { GeoJSONSource } from 'mapbox-gl';
import { Map as MapboxMap } from 'mapbox-gl';
import { DriverLocation } from '../mapbox-route-tracker';
import { RouteLocation } from 'types/common.consts';

const PROFILE_IMAGE_POINT_SOURCE_ID = 'profileImagePoint';
const CIRCLE_SOURCE_ID = 'circle';
const PROFILE_IMAGE_SOURCE_ID = 'profileImage';

export const drawImage = async ({
	map,
	profileImagePoint,
	image,
	driverLocation,
	route,
	routeIndex,
	color
}: {
	map: MapboxMap;
	profileImagePoint: GeoJSON.Feature<GeoJSON.Point>;
	image: ImageBitmap;
	driverLocation?: DriverLocation;
	route: RouteLocation[];
	routeIndex: number;
	color: string;
}): Promise<void> => {
	if (!map) return;
	if (map.getLayer(CIRCLE_SOURCE_ID)) {
		map.removeLayer(CIRCLE_SOURCE_ID);
	}
	if (map.getSource(CIRCLE_SOURCE_ID)) {
		map.removeSource(CIRCLE_SOURCE_ID);
	}
	map.addSource(CIRCLE_SOURCE_ID, {
		type: 'geojson',
		data: profileImagePoint
	});

	map.addLayer({
		id: CIRCLE_SOURCE_ID,
		source: CIRCLE_SOURCE_ID,
		type: 'circle',
		paint: {
			'circle-radius': 30,
			'circle-color': color,
			'circle-opacity': 0.3
		}
	});

	if (map.getLayer(PROFILE_IMAGE_POINT_SOURCE_ID)) {
		map.removeLayer(PROFILE_IMAGE_POINT_SOURCE_ID);
	}
	if (map.getSource(PROFILE_IMAGE_POINT_SOURCE_ID)) {
		map.removeSource(PROFILE_IMAGE_POINT_SOURCE_ID);
	}

	const isNewFunctionSignaturePostFirefox91 = createImageBitmap.length > 1;

	const fixedSizeImage = isNewFunctionSignaturePostFirefox91
		? await createImageBitmap(image, { resizeWidth: 100, resizeHeight: 100 })
		: await createImageBitmap(image);

	if (map.hasImage(PROFILE_IMAGE_SOURCE_ID)) {
		map.removeImage(PROFILE_IMAGE_SOURCE_ID);
	}
	//@ts-ignore
	map.addImage(PROFILE_IMAGE_SOURCE_ID, fixedSizeImage);

	map.addSource(PROFILE_IMAGE_POINT_SOURCE_ID, {
		type: 'geojson',
		data: profileImagePoint
	});

	map.addLayer({
		id: PROFILE_IMAGE_POINT_SOURCE_ID,
		source: PROFILE_IMAGE_POINT_SOURCE_ID,
		type: 'symbol',
		layout: {
			'icon-image': PROFILE_IMAGE_SOURCE_ID,
			'icon-rotation-alignment': 'map',
			'icon-allow-overlap': true,
			'icon-ignore-placement': true,
			'icon-size': 0.3,
			'icon-padding': 10
		}
	});

	updateProfileImagePoint({ map, profileImagePoint, driverLocation, route, routeIndex });
};

export const drawProfileImage = async ({
	map,
	profileImagePoint,
	profileImageUrl,
	driverLocation,
	route,
	routeIndex,
	color
}: {
	map: MapboxMap;
	profileImagePoint: GeoJSON.Feature<GeoJSON.Point>;
	profileImageUrl: string;
	driverLocation?: DriverLocation;
	route: RouteLocation[];
	routeIndex: number;
	color: string;
}): Promise<void> => {
	const avatar =
		'data:image/png;base64,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';
	const imageUrl = profileImageUrl && profileImageUrl !== '/images/avatar.png' ? profileImageUrl : avatar;
	map.loadImage(imageUrl, (error, image) => {
		if (error) {
			map.loadImage(avatar, (errorOnDefaultImageLoad, defaultImage) => {
				if (errorOnDefaultImageLoad) throw errorOnDefaultImageLoad;
				drawImage({ map, profileImagePoint, image: defaultImage, driverLocation, route, routeIndex, color });
			});
		} else {
			drawImage({ map, profileImagePoint, image, driverLocation, route, routeIndex, color });
		}
	});
};

export const updateProfileImagePoint = ({
	map,
	profileImagePoint,
	driverLocation,
	route,
	routeIndex
}: {
	map: MapboxMap;
	profileImagePoint: GeoJSON.Feature<GeoJSON.Point>;
	driverLocation?: DriverLocation;
	route: RouteLocation[];
	routeIndex: number;
}) => {
	if (!map) {
		return;
	}

	const center: [number, number] = driverLocation
		? [driverLocation.lng, driverLocation.lat]
		: [route[routeIndex]?.lng, route[routeIndex]?.lat];

	if (route.length) {
		map[routeIndex === 0 ? 'jumpTo' : 'flyTo']({
			center,
			zoom: DEFAULT_ZOOM,
			speed: DEFAULT_SPEED
		});
	}

	const sourcePoint = map.getSource(PROFILE_IMAGE_POINT_SOURCE_ID) as GeoJSONSource;
	const sourceCircle = map.getSource(CIRCLE_SOURCE_ID) as GeoJSONSource;

	profileImagePoint.geometry.coordinates = center;

	if (sourcePoint) {
		sourcePoint.setData(profileImagePoint);
	}
	if (sourceCircle) {
		sourceCircle.setData(profileImagePoint);
	}
};
